import { useState, useEffect, useCallback, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Search, X, Loader2 } from "lucide-react";

interface StorySearchProps {
  onSearch: (filters: {
    query?: string;
    age?: string;
    style?: string;
    language?: string;
  }) => void;
  totalResults?: number;
  isSearching?: boolean;
  initialFilters?: {
    query?: string;
    age?: string;
    style?: string;
    language?: string;
  };
}

export function StorySearch({ onSearch, totalResults, isSearching, initialFilters }: StorySearchProps) {
  const [query, setQuery] = useState(initialFilters?.query || "");
  const [age, setAge] = useState<string>(initialFilters?.age || "all");
  const [style, setStyle] = useState<string>(initialFilters?.style || "all");
  const [language, setLanguage] = useState<string>(initialFilters?.language || "all");
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  
  // Track if this is the first render to skip initial auto-search
  const isFirstRender = useRef(true);
  const onSearchRef = useRef(onSearch);

  // Update ref when onSearch changes
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  // Define handleSearch first to avoid temporal dead zone issues
  const handleSearch = useCallback(() => {
    const filters = {
      ...(debouncedQuery && { query: debouncedQuery }),
      ...(age && age !== 'all' && { age }),
      ...(style && style !== 'all' && { style }),
      ...(language && language !== 'all' && { language }),
    };
    
    onSearchRef.current(filters);
    setHasActiveFilters(Object.keys(filters).length > 0);
  }, [debouncedQuery, age, style, language]);

  // Debounce search query to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  // Auto-search when debounced query or filters change
  useEffect(() => {
    // Skip only the very first render to avoid triggering on mount
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    handleSearch();
  }, [debouncedQuery, age, style, language, handleSearch]);

  // Update local state when initialFilters change (e.g., from URL params)
  useEffect(() => {
    if (initialFilters) {
      setQuery(initialFilters.query || "");
      setAge(initialFilters.age || "all");
      setStyle(initialFilters.style || "all");
      setLanguage(initialFilters.language || "all");
    }
  }, [initialFilters]);

  const clearFilters = useCallback(() => {
    setQuery("");
    setAge("all");
    setStyle("all");
    setLanguage("all");
    setDebouncedQuery("");
    setHasActiveFilters(false);
    onSearchRef.current({});
  }, []);

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              {isSearching && debouncedQuery !== query && (
                <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 animate-spin" />
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Input
                    type="text"
                    placeholder="Search stories by title or content..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10 pr-10 border-purple-300 focus:border-purple-500"
                    data-testid="input-search"
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Search will update automatically as you type</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Select value={age} onValueChange={setAge}>
                  <SelectTrigger className="w-full md:w-[120px] border-purple-300" data-testid="select-age">
                    <SelectValue placeholder="Any age" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any age</SelectItem>
                    {Array.from({ length: 11 }, (_, i) => i + 2).map((ageValue) => (
                      <SelectItem key={ageValue} value={ageValue.toString()}>
                        Age {ageValue}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Filter stories by recommended age</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger className="w-full md:w-[140px] border-purple-300" data-testid="select-style">
                    <SelectValue placeholder="Any style" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any style</SelectItem>
                    <SelectItem value="funny">Funny</SelectItem>
                    <SelectItem value="adventurous">Adventurous</SelectItem>
                    <SelectItem value="gentle">Gentle</SelectItem>
                    <SelectItem value="magical">Magical</SelectItem>
                    <SelectItem value="educational">Educational</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Filter stories by their narrative style</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="w-full md:w-[140px] border-purple-300" data-testid="select-language">
                    <SelectValue placeholder="Any language" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any language</SelectItem>
                    <SelectItem value="en">🇺🇸 English</SelectItem>
                    <SelectItem value="es">🇪🇸 Español</SelectItem>
                    <SelectItem value="fr">🇫🇷 Français</SelectItem>
                    <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                    <SelectItem value="zh">🇨🇳 中文</SelectItem>
                    <SelectItem value="ja">🇯🇵 日本語</SelectItem>
                    <SelectItem value="pt">🇵🇹 Português</SelectItem>
                    <SelectItem value="it">🇮🇹 Italiano</SelectItem>
                    <SelectItem value="ru">🇷🇺 Русский</SelectItem>
                    <SelectItem value="ar">🇸🇦 العربية</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Filter stories by language</p>
            </TooltipContent>
          </Tooltip>

          <Button
            onClick={handleSearch}
            disabled={isSearching}
            className="bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700"
            data-testid="button-search"
          >
            {isSearching ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Search
          </Button>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={clearFilters}
                variant="outline"
                className="border-purple-300 hover:bg-purple-50"
                data-testid="button-clear-filters"
                asChild={false}
              >
                <X className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Reset all filters and show all stories</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {totalResults !== undefined && (
          <div className="flex items-center justify-between text-sm text-gray-600">
            <p data-testid="text-search-results">
              {hasActiveFilters ? (
                <>
                  Found <span className="font-semibold text-purple-600">{totalResults}</span> {totalResults === 1 ? 'story' : 'stories'} matching your search
                </>
              ) : (
                <>
                  Showing <span className="font-semibold text-purple-600">{totalResults}</span> {totalResults === 1 ? 'story' : 'stories'}
                </>
              )}
            </p>
            {isSearching && (
              <div className="flex items-center gap-2 text-purple-600">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span className="text-xs">Searching...</span>
              </div>
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}